'use client';
/** @jsxImportSource @emotion/react */

import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import type { MenuProps } from '../../../menu/Menu';
import type { PaperProps } from '../../../paper/Paper';

import { Box } from '../../../box/Box';
import { CoreIcon } from '../../../core-icon/CoreIcon';
import { DialogContent } from '../../../dialog-content/DialogContent';
import { DialogTitle } from '../../../dialog-title/DialogTitle';
import { Dialog } from '../../../dialog/Dialog';
import { Divider } from '../../../divider/Divider';
import { IconButton } from '../../../icon-button/IconButton';
import { InputAdornment } from '../../../input-adornment/InputAdornment';
import {
  MenuList,
  type MenuListProps as ZeusMenuListProps,
} from '../../../menu-list/MenuList';
import { TextField } from '../../../text-field/TextField';
import { useTheme } from '../../../theme/useTheme';
import { useMediaQuery } from '../../../utils/UseMediaQuery';
import { FilterableMenu } from '../filterable-menu/FilterableMenu';
import { DropdownVirtualizedList } from './components/DropdownVirtualizedList';
import { DropdownContext } from './dropdown-context';

export type DropdownProps<T> = Pick<MenuProps, 'transformOrigin'> & {
  /**
   * The element to anchor the dropdown menu to
   */
  anchorEl: HTMLElement | null;
  /**
   * The content to render inside the dropdown or dialog
   */
  children: ReactNode | ((items: T[]) => ReactNode);
  /**
   * List of items to be displayed in the dropdown
   */
  items: T[];
  /**
   * Ref for the button that opens the dropdown
   */
  menuButtonRef: React.RefObject<HTMLButtonElement | null>;
  /**
   * Close the dropdown menu or dialog
   */
  onClose: (event: Event | React.SyntheticEvent) => void;
  /**
   * The extra props for the slot component paper.
   */
  slotProps?: {
    paper?: PaperProps;
  };
  /**
   * Aria-label for close button
   */
  menuCloseButtonLabel?: string;
  /**
   * Message to display when there are no items
   */
  menuEmptyResultElement?: ReactNode;
  /**
   * Props applied to the MenuList element
   */
  MenuListProps?: Omit<ZeusMenuListProps, 'onKeyDown' | 'role'>;
  /**
   * Label for the search input field
   */
  menuSearchLabel?: string;
  /**
   * Title for the country code dropdown
   */
  menuTitle?: string;
  /**
   * Optional filter function to filter items based on search query
   */
  onFilter?: (item: T, query: string) => boolean;
  /*
   * Callback function to handle menu item click
   */
  onMenuItemClick?: (item: T) => void;
};

export function Dropdown<T>({
  anchorEl,
  children,
  items,
  menuButtonRef,
  menuCloseButtonLabel,
  menuEmptyResultElement,
  MenuListProps,
  menuSearchLabel,
  menuTitle,
  onClose,
  onFilter,
  onMenuItemClick,
  slotProps,
}: DropdownProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [focusIndex, setFocusIndex] = useState<number | undefined>(-1);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        onFilter
          ? onFilter(item, searchQuery)
          : String(item).toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [searchQuery, items, onFilter]
  );

  const renderChildren = (() => {
    if (filteredItems.length === 0) {
      return menuEmptyResultElement;
    }
    if (typeof children === 'function') {
      return children(filteredItems);
    }
    return children;
  })();

  if (isMobile) {
    return (
      <DropdownContext.Provider
        value={{
          filteredItems,
          focusIndex,
          isMobile,
          MenuListProps,
          onMenuItemClick,
          setFocusIndex,
        }}
      >
        <Dialog
          PaperProps={{
            sx: {
              bottom: 0,
              height: '90vh',
              left: 0,
              m: 0,
              maxHeight: '560px',
              position: 'fixed',
              right: 0,
              width: '100vw',
            },
          }}
          open={Boolean(anchorEl)}
          fullWidth
          onClose={onClose}
        >
          <IconButton
            sx={{
              position: 'absolute',
              right: theme.spacing(1),
              top: theme.spacing(1),
            }}
            aria-label={menuCloseButtonLabel}
            onClick={onClose}
          >
            <CoreIcon name="cross" />
          </IconButton>
          <DialogTitle sx={{ p: theme.spacing(1.5, 2, 0) }}>
            {menuTitle}
          </DialogTitle>
          <Box sx={{ padding: theme.spacing(2), width: '100%' }}>
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CoreIcon name="search" />
                  </InputAdornment>
                ),
              }}
              autoComplete="off" // Disable autocomplete to prevent browser autofill
              label={menuSearchLabel}
              margin="none"
              size="small"
              value={searchQuery}
              variant="outlined"
              autoFocus
              fullWidth
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                setFocusIndex?.(-1);
              }}
            />
          </Box>
          <Divider />
          <DialogContent sx={{ padding: 0 }}>
            <MenuList
              {...MenuListProps}
              sx={{
                flex: 1,
                height: '100%',
                overflowY: 'auto',
                padding: 0,
                ...MenuListProps?.sx,
              }}
            >
              {renderChildren}
            </MenuList>
          </DialogContent>
        </Dialog>
      </DropdownContext.Provider>
    );
  }

  return (
    <DropdownContext.Provider
      value={{
        filteredItems,
        focusIndex,
        isMobile,
        MenuListProps,
        onMenuItemClick,
        setFocusIndex,
      }}
    >
      <FilterableMenu
        anchorEl={anchorEl}
        menuButtonRef={menuButtonRef}
        MenuListProps={MenuListProps}
        menuSearchLabel={menuSearchLabel}
        open={Boolean(anchorEl)}
        searchQuery={searchQuery}
        slotProps={slotProps}
        onClose={onClose}
        onFocusedChange={(index) => setFocusIndex(index)}
        onSearch={(query) => setSearchQuery(query)}
      >
        {renderChildren}
      </FilterableMenu>
    </DropdownContext.Provider>
  );
}

Dropdown.VirtualizedList = DropdownVirtualizedList;
